import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { apiInstance, configureApiInstance } from './apiInstance';
import { ApiRoutes } from './apiRoutes';
import { clearSessionStorage, saveToken, saveUrlToken } from '../utils/sessionStorage';
import { PageRoutes } from '@/app/components/router/routes';

let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

const getUrlToken = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('t');
};

const refreshBrToken = async (): Promise<string> => {
  const urlToken = getUrlToken();
  if (!urlToken) throw new Error('No URL token available');

  const response = await apiInstance.post(`${ApiRoutes.Auth}?token=${urlToken}`);
  if (!response.data) throw new Error('Failed to refresh BR token');

  configureApiInstance({ token: response.data });
  saveToken(response.data);
  saveUrlToken(urlToken);
  return response.data;
};

const handleBrTokenRefresh = async (): Promise<string> => {
  if (isRefreshing && refreshPromise) return refreshPromise;

  isRefreshing = true;
  refreshPromise = refreshBrToken().finally(() => {
    isRefreshing = false;
    refreshPromise = null;
  });
  return refreshPromise;
};

const withBrTokenRefresh = async <T>(
  request: () => Promise<AxiosResponse<T>>
): Promise<AxiosResponse<T>> => {
  try {
    return await request();
  } catch (error: any) {
    if (error.response?.status === 400 || error.response?.status === 403) {
      try {
        clearSessionStorage();
        await handleBrTokenRefresh();
        return await request();
      } catch (refreshError) {
        clearSessionStorage();
        window.location.replace(PageRoutes.AUTH_ERROR);
        throw refreshError;
      }
    }
    throw error;
  }
};

export const brGet = <T = any>(
  url: string,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return withBrTokenRefresh(() => apiInstance.get<T>(url, config));
};

export const brPost = <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return withBrTokenRefresh(() => apiInstance.post<T>(url, data, config));
};

export { handleBrTokenRefresh as getTokenAsyncWithDedup };
